import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations";

const STORAGE_KEY = "bet_lang";
const LanguageContext = createContext(null);

function readStoredLang() {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === "en" || saved === "vi" ? "en" : "tr";
}

function lookup(dict, key) {
  return key.split(".").reduce((node, part) => (node && node[part] != null ? node[part] : null), dict);
}

function interpolate(text, vars = {}) {
  return String(text).replace(/\{\{(\w+)\}\}/g, (_, name) => (vars[name] != null ? vars[name] : ""));
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readStoredLang);

  const setLang = useCallback((next) => {
    const value = next === "en" ? "en" : "tr";
    setLangState(value);
    localStorage.setItem(STORAGE_KEY, value);
    document.documentElement.lang = value;
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key, vars) => {
      const pack = translations[lang] || translations.tr;
      const fallback = translations.tr;
      const text = lookup(pack, key) ?? lookup(fallback, key) ?? key;
      return interpolate(text, vars);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}

export function currentLanguage() {
  return readStoredLang();
}

export function localeTag(lang = readStoredLang()) {
  return lang === "en" ? "en-US" : "tr-TR";
}
